import { Input } from "ant-design-vue";
import lodash from "lodash";
import { reactive } from "vue";
import {
    createColumnDate,
    createColumnInlineTextArea,
    createColumnInput,
    createColumnInputNumber,
    createColumnSelect
} from "../../../../../../components/table/c-table.logic";
import i18n from "../../../../../../i18n";
import store from "../../../../../../store";
import { createLoading } from "../../../../../../utils/loading";
import { createManageExportCsv, createPage } from "../../../../../../utils/page";
import { callProductType } from "../../../../company/company/company.logic";
import { callCountry } from "../../../../master-mgt/country/country.logic";
import { callLabTopic } from "../../../../master-mgt/lab/sampling-monitor-type-lab-topic/sampling-monitor-type-lab-topic.logic";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/samplingProductAnalysisTopic";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callCompanyByNameEn = ({ searchQuery }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTmp", type: "Boolean", operator: "=", value: false }
    ];
    if (searchQuery) {
        where.push({ name: "nameEn", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/company/search", { criteria: { select: ["*"], page: 0, size: 1000000, where, sort: [] } })
        .then(page => page.data);
};
export const callLabAnalysysStatus = () => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LAB_ANALYSIS_STATUS" });
export const callDepartment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAll", {
        searchQuery,
        where: [
            { name: "refDepartmentType.key", type: "String", operator: "=", value: "LAB" },
            { name: "refLabType.key", type: "String", operator: "=", value: "labPhysic" }
        ]
    });
export const callLabExt = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/labExt/cacheFindAll", {
        searchQuery,
        where: [{ name: "isPhysic", type: "Boolean", operator: "=", value: true }]
    });

export const onSearch = ({ page, props }) => {
    const { refLabTypeKey } = props;
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const countryIndex = customWhere.findIndex(d => d.name?.startsWith("refSamplingProductAnalysis.refSamplingProduct.refCountry1"));
    if (countryIndex !== -1) {
        const countryWhere = customWhere[countryIndex];
        customWhere.splice(
            countryIndex,
            1,
            {
                openBucket: true,
                name: "refSamplingProductAnalysis.refSamplingProduct.refCountry1.id",
                type: "UUID",
                operator: "=",
                value: countryWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refSamplingProductAnalysis.refSamplingProduct.refCountry2.id",
                type: "UUID",
                operator: "=",
                value: countryWhere.value
            }
        );
    }
    const departmentIndex = customWhere.findIndex(d => d.name?.startsWith("refSamplingProductAnalysis.refDepartment"));
    if (departmentIndex !== -1) {
        const departmentWhere = customWhere[departmentIndex];
        customWhere.splice(
            departmentIndex,
            1,
            {
                openBucket: true,
                name: "refSamplingProductAnalysis.refDepartment.id",
                type: "UUID",
                operator: "=",
                value: departmentWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refSamplingProductAnalysis.refLabExt.id",
                type: "UUID",
                operator: "=",
                value: departmentWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: [
                ...customWhere,
                { name: 'refSamplingProductAnalysis.refDepartment.refLabType.key', type: 'String', operator: '=', value: refLabTypeKey },
                { name: "isActive", type: "Boolean", operator: "=", value: true },
                { name: "transferTos.id", type: "UUID", operator: "=", value: null },
                { name: "refLabTopic.nameS", type: "String", operator: "!in", value: ["COL", "FLAV", "TXT", "ODOR", "CLRN"] }
            ],
            sort,
            children: [
                {
                    name: "children",
                    select: ["*", "refLabTopic.*", "refTopicAnalysisStatus.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
                {
                    name: "inputs",
                    select: ["*", "refAcceptanceStatus.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                }
            ]
        }
    })
        .then(page => {
            page.data.forEach(d => {
                if (d.refLabTopic?.refLabFormula?.key === "Weight") {
                    d.remark = d.inputs
                        .filter(d2 => parseFloat(d2.realValue) < parseFloat(d.t2))
                        .map(d2 => d2.realValue)
                        .join(", ");
                } else if (d.refLabTopic?.refLabFormula?.key === "Integrity") {
                    d.remark = d.inputs
                        .filter(d2 => ["Reject"].includes(d2.refAcceptanceStatus?.key))
                        .map(d2 => d2.point)
                        .join(", ");
                } else if (d.refLabTopic?.refLabFormula?.key === "Acceptance") {
                    d.remark = d.children
                        .filter(d2 => ["Reject"].includes(d2.refTopicAnalysisStatus?.key))
                        .map(d2 => d2.refLabTopic?.name)
                        .join(", ");
                }
            });
            return page;
        })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const createColumns = () => {
    const columns = [
        createColumnDate({
            title: () => i18n.global.t("model.samplingProduct.samplingDate"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.refSamplingRequest.samplingCollectDate"
        }),
        createColumnDate({
            visible: () => false,
            title: () => i18n.global.t("model.samplingProductAnalysis.analysisStartDate"),
            dataIndex: "refSamplingProductAnalysis.analysisStartDate"
        }),
        createColumnSelect({
            visible: () => false,
            title: () => i18n.global.t("views.report.labPhysic.department"),
            dataIndex: "refSamplingProductAnalysis.refDepartment.nameTh",
            customRender: ({ record }) => (
                <div>{record.refSamplingProductAnalysis.refDepartment.nameTh || record.refSamplingProductAnalysis.refLabExt.nameTh}</div>
            ),
            inputProp: () => ({
                list: ({ searchQuery }) =>
                    Promise.all([callDepartment({ searchQuery }), callLabExt({ searchQuery })]).then(arr => [...arr[0], ...arr[1]]),
                optLabel: "nameTh"
            }),
            search: { name: "refSamplingProductAnalysis.refDepartment.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("views.report.labPhysic.company"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.refSamplingRequest.refCompany.nameEn",
            inputProp: () => ({ list: callCompanyByNameEn, optLabel: "nameEn" }),
            search: { name: "refSamplingProductAnalysis.refSamplingProduct.refSamplingRequest.refCompany.id" },
            align: "center"
        }),
        createColumnInputNumber({
            title: () => i18n.global.t("model.samplingProductAnalysis.samplingAmount"),
            dataIndex: "refSamplingProductAnalysis.samplingAmount",
            search: { visible: () => false }
        }),
        createColumnInput({
            title: () => i18n.global.t("views.report.labPhysic.samplingNo"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.samplingNo",
            align: "center",
            search: {
                operator: "between",
                getValue2: ({ field }) => field.searchValue2,
                field: {
                    inputRender: ({ field }) => {
                        return (
                            <Input.Group compact>
                                <Input
                                    style={{ width: "45%" }}
                                    value={field.searchValue}
                                    onUpdate:value={value => (field.searchValue = value)}
                                ></Input>
                                <span class="text-center v-middle" style={{ width: "10%" }}>
                                    {i18n.global.t("views.report.labPhysic.to")}
                                </span>
                                <Input
                                    style={{ width: "45%" }}
                                    value={field.searchValue2}
                                    onUpdate:value={value => (field.searchValue2 = value)}
                                ></Input>
                            </Input.Group>
                        );
                    }
                }
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("views.report.labPhysic.country"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.refCountry1.nameEn",
            customRender: ({ record }) => (
                <div>
                    {record.refSamplingProductAnalysis?.refSamplingProduct?.refCountry1?.nameEn}{" "}
                    {record.refSamplingProductAnalysis?.refSamplingProduct?.refCountry2?.nameEn
                        ? `, ${record.refSamplingProductAnalysis.refSamplingProduct.refCountry2.nameEn}`
                        : ""}
                </div>
            ),
            inputProp: () => ({ list: callCountry, optLabel: "nameEn" }),
            search: { name: "refSamplingProductAnalysis.refSamplingProduct.refCountry1.id" },
            align: "center"
        }),
        createColumnSelect({
            visible: () => false,
            title: () => i18n.global.t("views.report.labPhysic.productType"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.refCompanyProduct.refProduct.refProductType.value",
            inputProp: () => ({ list: callProductType, optLabel: "value" }),
            search: { name: "refSamplingProductAnalysis.refSamplingProduct.refCompanyProduct.refProduct.refProductType.id" }
        }),
        createColumnInput({
            title: () => i18n.global.t("views.report.labPhysic.product"),
            dataIndex: "refSamplingProductAnalysis.refSamplingProduct.refCompanyProduct.refProduct.nameEn",
            search: { visible: () => false },
            align: "center",
        }),
        createColumnSelect({
            title: () => i18n.global.t("views.report.labPhysic.topic"),
            dataIndex: "refLabTopic.name",
            inputProp: () => ({
                list: ({ searchQuery }) => callLabTopic({ searchQuery, labTypeKey: "labPhysic" }),
                optLabel: "name",
                mode: "multiple"
            }),
            search: {
                name: "refLabTopic.id",
                operator: "in",
                getValue: ({ value }) => value.map(d => d.id),
                field: { searchValue: [] }
            },
            align: "center",
        }),
        createColumnSelect({
            title: () => i18n.global.t("views.report.labPhysic.result"),
            dataIndex: "refSamplingProductAnalysis.refLabAnalysisStatus.value",
            inputProp: () => ({ list: callLabAnalysysStatus, optLabel: "value" }),
            search: { name: "refSamplingProductAnalysis.refLabAnalysisStatus.id" },
            align: "center"
        }),
        // createColumnInlineTextArea({
        //     title: () => i18n.global.t("views.report.labPhysic.remark"),
        //     dataIndex: "remark",
        //     customRenderInputProp: () => ({ disabled: true }),
        //     search: { visible: () => false }
        // }),
        createColumnInlineTextArea({
            title: () => i18n.global.t("views.report.labPhysic.remark"),
            dataIndex: "rejectRemark",
            customRenderInputProp: () => ({ disabled: true }),
            search: { visible: () => false }
        })
    ];
    return columns;
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [createManageExportCsv({ menuContext })];
    state.manageExtras = [];
    state.rowActions = [];
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refSamplingProductAnalysis.refSamplingProduct.refSamplingRequest.samplingCollectDate",
                "refSamplingProductAnalysis.refSamplingProduct.refSamplingRequest.refCompany.nameEn",
                "refSamplingProductAnalysis.refSamplingProduct.refCountry1.nameEn",
                "refSamplingProductAnalysis.refSamplingProduct.refCountry2.nameEn",
                "refSamplingProductAnalysis.refSamplingProduct.refCompanyProduct.refProduct.nameEn",
                "refSamplingProductAnalysis.samplingAmount",
                "refSamplingProductAnalysis.refSamplingProduct.samplingNo",
                "refLabTopic.name",
                "refSamplingProductAnalysis.refLabAnalysisStatus.value",
                "refSamplingProductAnalysis.refSamplingProduct.refCompanyProduct.refProduct.refProductType.value",
                "refSamplingProductAnalysis.refDepartment.nameTh",
                "refSamplingProductAnalysis.refLabExt.nameTh",
                "refLabTopic.*",
                "refLabTopic.refLabFormula.*"
            ]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } }
});
